/*======================================================================================================================
  * Bluebell: src/app/state/image-deltas/image-deltas.reducer.ts
  * Last Edited: 17/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import {
  addImageDeltaFilter,
  clearImageDeltaAvailableVariables,
  listImageDeltaAvailableVariables,
  listImageDeltaAvailableVariablesFailure,
  listImageDeltaAvailableVariablesSuccess,
  listImageDeltas,
  listImageDeltasFailure,
  listImageDeltasSuccess,
  patchImageDeltaFilterState,
  removeImageDeltaFilter,
  resetImageDeltaFilterState,
  setImageDeltaFilterState,
} from '@state/image-deltas/image-deltas.actions';
import {
  getDefaultImageDeltaFilterState,
  ImageDelta,
  ImageDeltaFilters,
  ImageDeltaFilterState,
} from '@state/image-deltas/image-deltas.interfaces';
import { postproViewerClosed, selectPostproVariable } from '@state/postpro/postpro.actions';
import { deselectAllResults } from '@state/results/results.actions';


export interface State extends EntityState<ImageDelta> {
  loading: boolean;
  availableVariables: string[];
  availableVariablesLoading: boolean;
  filters: ImageDeltaFilters|null;
  filterState: ImageDeltaFilterState;
}

export const adapter = createEntityAdapter<ImageDelta>({
  selectId: (delta: ImageDelta) => delta.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  loading: false,
  availableVariables: [],
  availableVariablesLoading: false,
  filters: null,
  filterState: getDefaultImageDeltaFilterState()
});

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({...initialState});
  }),

  on(listImageDeltas, (state) => {
    return adapter.removeAll({ ...state, loading: true });
  }),

  on(listImageDeltasSuccess, (state, { data, filters }) => {
    return adapter.upsertMany(
      data,
      {
        ...state,
        filters,
        filterState: getDefaultImageDeltaFilterState(filters, state.filterState),
        loading: false
      }
    );
  }),

  on(listImageDeltasFailure, (state) => {
    return adapter.removeAll({ ...state, loading: false });
  }),

  on(listImageDeltaAvailableVariables, (state) => {
    return { ...state, availableVariablesLoading: true };
  }),

  on(listImageDeltaAvailableVariablesSuccess, (state, { availableVariables }) => {
    return { ...state, availableVariables, availableVariablesLoading: false };
  }),

  on(listImageDeltaAvailableVariablesFailure, (state) => {
    return { ...state, availableVariables: [], availableVariablesLoading: false };
  }),

  // Forces Actions

  on(
    clearImageDeltaAvailableVariables,
    deselectAllResults,
    (state) => {
    return adapter.removeAll({ ...state, availableVariables: [] });
  }),

  /**
   * Component Actions
   */
  on(selectPostproVariable, (state, { postproType }) => {
    if (postproType === 'image_delta') {
      return state;
    } else {
      return adapter.removeAll({
        ...state,
        filters: null,
        filterState: getDefaultImageDeltaFilterState(),
      });
    }
  }),

  on(addImageDeltaFilter, (state, { filter, value }) => {
    return {
      ...state,
      filterState: {
        ...state.filterState,
        [filter]: [...state.filterState[filter], value],
      },
    };
  }),

  on(removeImageDeltaFilter, (state, { filter, value }) => {
    return {
      ...state,
      filterState: {
        ...state.filterState,
        [filter]: state.filterState[filter].filter(selected => selected !== value),
      },
    };
  }),

  on(patchImageDeltaFilterState, (state, { partialFilterState }) => {
    return {
      ...state,
      filterState: {
        ...state.filterState,
        ...partialFilterState,
      },
    };
  }),

  on(setImageDeltaFilterState, (state, { filterState }) => {
    return {
      ...state,
      filterState,
    };
  }),

  on(resetImageDeltaFilterState, (state) => {
    return {
      ...state,
      filterState: getDefaultImageDeltaFilterState(),
    };
  }),

  on(postproViewerClosed, (state) => {
    return adapter.removeAll({
      ...initialState,
      availableVariables: state.availableVariables,
    });
  }),
);

export const {
  selectEntities: getEntities,
  selectTotal: getTotal,
  selectAll: getAll,
  selectIds: getIds,
} = adapter.getSelectors();
export const featureKey = 'imageDeltas';
export const getLoading = (state: State) => state.loading;
export const getAvailableVariables = (state: State) => state.availableVariables;
export const getAvailableVariablesLoading = (state: State) => state.availableVariablesLoading;
export const getFilters = (state: State) => state.filters;
export const getFilterState = (state: State) => state.filterState;
